import React from 'react';
import { Box, Card, Divider, Skeleton, Stack, Switch, Typography } from '@mui/material';
import { LenderApprovalTermsBasis, LenderApprovalTermsSummaryViewModel } from '@trnsact/trnsact-shared-types';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { APPROVAL_TERMS_SUMMARY_CARDS } from '../../../../constants';

interface Props {
  summary: LenderApprovalTermsSummaryViewModel | null;
  loading?: boolean;
  basis: LenderApprovalTermsBasis;
  onBasisChange: (basis: LenderApprovalTermsBasis) => void;
}

export const ApprovalTermsSummaryCards = ({ summary, loading, basis, onBasisChange }: Props) => (
  <CardContainer
    title="Summary cards"
    overTitleActionBtn={
      <Stack data-pdf-exclude direction="row" gap="4px" alignItems="center">
        <Typography
          variant={basis === LenderApprovalTermsBasis.AllApprovals ? 'subtitle2' : 'body2'}
          color={basis === LenderApprovalTermsBasis.AllApprovals ? 'primary' : 'text.primary'}
          sx={{ textTransform: 'none' }}
        >
          All approvals
        </Typography>
        <Switch
          size="small"
          color="primary"
          checked={basis === LenderApprovalTermsBasis.FundedOnly}
          onChange={(_, checked) =>
            onBasisChange(checked ? LenderApprovalTermsBasis.FundedOnly : LenderApprovalTermsBasis.AllApprovals)
          }
        />
        <Typography
          variant={basis === LenderApprovalTermsBasis.FundedOnly ? 'subtitle2' : 'body2'}
          color={basis === LenderApprovalTermsBasis.FundedOnly ? 'primary' : 'text.primary'}
          sx={{ textTransform: 'none' }}
        >
          Funded deals only
        </Typography>
      </Stack>
    }
  >
    <Box
      sx={{
        gap: '1rem',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(3, 1fr)' },
      }}
    >
      {APPROVAL_TERMS_SUMMARY_CARDS.map(({ key, title, formatValue, formatRange }) => {
        const metric = summary?.[key] ?? null;

        return (
          <Card key={key} variant="outlined" sx={{ p: 2 }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography fontWeight={500}>{title}:</Typography>
              <Typography variant="h6">
                {loading ? <Skeleton width={60} /> : metric ? formatValue(metric.avg) : '—'}
              </Typography>
            </Stack>

            <Divider sx={{ my: 1 }} />

            <Typography variant="body2" color="text.secondary">
              {loading ? <Skeleton width={120} /> : metric ? formatRange(metric.min, metric.max) : ''}
            </Typography>
          </Card>
        );
      })}
    </Box>
  </CardContainer>
);
